import type { TStreakItem } from '@/src/elements/streak/streak.type'
import { fetcher } from '@/src/general/utilities/fetcher'
import type { TSignalConfig } from '@/src/general/utilities/fetcher/IFetcher'

import { INTENTION_TYPE } from '../models/Intention.enums'
import type { TTypeIntention } from '../models/Intention.types'
import type { TCreateIntentionPayload } from './types/createIntentionApi.types'
import type { TCreateUpliftPayload, TUpliftApi } from './types/createUpliftApi.types'
import type { TDeleteIntentionPayload } from './types/deleteIntentionApi.types'
import type { TEditIntentionApi, TEditIntentionPayload } from './types/editIntentionApi.types'
import type {
  TGetIntentionPayload,
  TMediumIntentionApi,
  TRandomPopularIntentionApi,
  TTypeIntentionApi,
} from './types/getIntentionApi.types'
import type { TIntentionPrayerApiPayload, TPrayersApi } from './types/getIntentionPrayersApi.types'
import type { TGetIntentionsPayload } from './types/getIntentionsApi.types'
import type { TPrayersOnIntentionApi } from './types/getPrayersOnIntentionApi.types'
import type { TRandomPopularIntentionsPayload } from './types/getRandomPopularIntentionApi.types'
import type { TWatchmanDailyIntentionApi } from './types/getWatchmanIntentionsApi.types'
import type { TIntentionThemeApi } from './types/intentionThemeApi.types'

export const intentionApiToEntity = <Type extends INTENTION_TYPE>(type: Type, intentionApi: TTypeIntentionApi<Type>) =>
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- distributive indexed access
  ({ ...intentionApi, type }) as TTypeIntention<Type>

export const intentionApi = {
  async getIntentions(payload: TGetIntentionsPayload, { signal }: TSignalConfig) {
    const intentions = await fetcher.query<TMediumIntentionApi[]>('/intentions', {
      signal,
      params: {
        level: payload.level ?? 0,
        widgetId: payload.widgetId,
        exceptIntentionsIds: payload.exceptIntentionsIds,
        landingPageSlug: payload.landingPageSlug,
      },
    })
    return intentions.map((intention) => intentionApiToEntity(INTENTION_TYPE.INTENTION, intention))
  },

  async getIntentionsByType<Type extends INTENTION_TYPE.ARRIVAL | INTENTION_TYPE.BIRTHDAY>(
    type: Type,
    { signal }: TSignalConfig,
  ) {
    const intentions = await fetcher.query<TTypeIntention<Type>[]>(`/${type}-intentions`, {
      headers: { 'Cache-Control': 'no-cache' },
      signal,
      params: {
        limit: 1,
      },
    })
    return intentions.map((intention) => intentionApiToEntity(type, intention))
  },

  async getIntention<Type extends INTENTION_TYPE>(
    { intentionId, shareToken, type }: TGetIntentionPayload<Type>,
    { signal }: TSignalConfig,
  ) {
    const intention = await fetcher.query<TTypeIntentionApi<Type>>(`/intention/${intentionId}`, {
      signal,
      params: { shareToken, type },
    })
    return intentionApiToEntity(type, intention)
  },

  async getRandomPopularIntentionsQuery(payload: TRandomPopularIntentionsPayload, { signal }: TSignalConfig) {
    return fetcher.query<TRandomPopularIntentionApi[]>('/intention/random-popular', { signal, params: payload })
  },

  async getIntentionPrayers(
    { intentionId, type, offset, limit }: TIntentionPrayerApiPayload,
    { signal }: TSignalConfig,
  ) {
    return fetcher.query<TPrayersApi[]>(`/prayers`, {
      params: {
        'toIntention.id': intentionId,
        type,
        offset,
        limit,
      },
      signal,
    })
  },

  async createIntention(payload: TCreateIntentionPayload) {
    const intention = await fetcher.mutate<TMediumIntentionApi, TCreateIntentionPayload>('/intention', payload, {
      method: 'POST',
    })
    return intentionApiToEntity(INTENTION_TYPE.INTENTION, intention)
  },

  editIntention(intentionId: number, payload: TEditIntentionPayload) {
    return fetcher.mutate<TEditIntentionApi, TEditIntentionPayload>(`/intention/${intentionId}`, payload, {
      method: 'PATCH',
    })
  },

  deleteIntention(intentionId?: number, payload?: TDeleteIntentionPayload) {
    return fetcher.mutate<void, TDeleteIntentionPayload>(`/intention${intentionId ? '/' + intentionId : ''}`, payload, {
      method: 'DELETE',
    })
  },

  createUplift(payload: TCreateUpliftPayload) {
    return fetcher.mutate<TUpliftApi, TCreateUpliftPayload>('/uplifts', payload, {
      method: 'POST',
    })
  },

  async markIntentionAsShared(intentionId: number) {
    const intention = await fetcher.mutate<TMediumIntentionApi>(
      `/intention/${intentionId}/shared`,
      {},
      {
        method: 'PATCH',
      },
    )
    return intentionApiToEntity(INTENTION_TYPE.INTENTION, intention)
  },
  markIntentionsAsViewed(ids: number[]) {
    return fetcher.mutate<void, { ids: number[] }>('/intentions/views', { ids }, { method: 'POST' })
  },
  getIntentionTheme: (slug: string, { signal }: TSignalConfig) =>
    fetcher.query<TIntentionThemeApi>(`/intention-landing-page?slug=${slug}`, { signal }),

  getWatchmanIntentions: ({ signal }: TSignalConfig) =>
    fetcher.query<TWatchmanDailyIntentionApi[]>('/watchmen/intentions', { signal }),

  getPrayersOnIntentions: (
    { intentionIds, intentionTypes }: { intentionIds: number[]; intentionTypes: INTENTION_TYPE[] },
    { signal }: TSignalConfig,
  ) =>
    fetcher.query<TPrayersOnIntentionApi>('/prayers-on-intentions', {
      params: {
        intentionIds: intentionIds,
        intentionTypes: intentionTypes,
      },
      signal,
    }),

  refuseIntentionWatchman: ({ id }: { id: number }) =>
    fetcher.mutate<TWatchmanDailyIntentionApi, void>(`/assign-watchman/refuse/${id}`, undefined, { method: 'PUT' }),

  getStreaksWatchman: (
    { from, to, orderBy }: { from: string; to: string; orderBy: 'asc' | 'desc' },
    { signal }: TSignalConfig,
  ) => fetcher.query<TStreakItem[]>('/streaks/watchman', { params: { from, to, orderBy }, signal }),
}
